import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import Icon from "../../common/elements/Icon";
import Button from "../../common/elements/Button";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import InfoCard from "../../common/elements/InfoCard";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { serverErrMsg } from "../utils/validators";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { useThor } from "../context/ThorContext";
import AudienceModal from "./AudienceModal";
import ConfirmDialog from "./ConfirmDialog";

const OPERATION_LABELS = {
    IN: 'Any value in the list',
    GT: 'Greater than',
    GTE: 'Greater than or equal to',
    LT: 'Less than',
    LTE: 'Less than or equal to',
    BTWN: 'Between',
};

export const AudiencesTab = ({ experimentKey }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();

    const [audiencesState, setAudiencesState] = useState(buildLoadingState([]));
    const [modalOpen, setModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [audienceDescription, setAudienceDescription] = useState('');
    const [isSavingDesc, setIsSavingDesc] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const fetchAudiences = useCallback(() => {
        setAudiencesState(buildLoadingState([]));
        execute('GET', thor_api_urls.experimentAudiences(experimentKey))
            .then(data => setAudiencesState(buildDataState(data?.experimentAudiences ?? data ?? [])))
            .catch(error => {
                console.error("Failed to fetch audiences:", error);
                setAudiencesState(buildErrorState(error, []));
            });
    }, [execute, experimentKey]);

    const unwrapExperiment = (raw) =>
        (raw?.experiment && typeof raw.experiment === 'object') ? raw.experiment : raw;

    useEffect(() => {
        fetchAudiences();
        execute('GET', thor_api_urls.experiment(experimentKey))
            .then(data => setAudienceDescription(unwrapExperiment(data)?.audienceDescription ?? ''))
            .catch(() => {});
    }, [fetchAudiences, execute, experimentKey]);

    const handleSaveDescription = () => {
        setIsSavingDesc(true);
        execute('GET', thor_api_urls.experiment(experimentKey))
            .then(raw => {
                const current = unwrapExperiment(raw);
                const payload = {
                    ...current,
                    // GET uses different field names than PUT for these required fields
                    jiraTicket: current.jiraTicket ?? current.ticket ?? '',
                    specUrl: current.specUrl ?? current.specification ?? '',
                    productManager: current.productManager ?? current.owner ?? '',
                    audienceDescription,
                };
                return execute('PUT', thor_api_urls.experiment(experimentKey), payload);
            })
            .then(() => addToast('Audience description saved.', 'success'))
            .catch(err => addToast(serverErrMsg(err, 'Failed to save description.'), 'danger'))
            .finally(() => setIsSavingDesc(false));
    };

    const handleSubmit = (form) => {
        setIsSubmitting(true);
        execute('POST', thor_api_urls.experimentAudiences(experimentKey), form)
            .then(() => {
                setModalOpen(false);
                fetchAudiences();
                addToast('Audience added.', 'success');
            })
            .catch(error => {
                console.error("Failed to add audience:", error);
                addToast(serverErrMsg(error, 'Failed to add audience.'), 'danger');
            })
            .finally(() => setIsSubmitting(false));
    };

    const handleDelete = (audience) => {
        setDeletingId(audience.audienceId);
        execute('DELETE', thor_api_urls.experimentAudience(experimentKey, audience.audienceId))
            .then(() => {
                fetchAudiences();
                addToast('Audience removed.', 'success');
            })
            .catch(error => {
                console.error("Failed to remove audience:", error);
                addToast(serverErrMsg(error, 'Failed to remove audience.'), 'danger');
            })
            .finally(() => setDeletingId(null));
    };

    const audiences = audiencesState.data || [];

    return (
        <>
            <div className="level mb-3">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">
                        Audiences<HelpTooltip text={HELP.audiences.general} />
                    </p>
                </div>
                <div className="level-right">
                    <Button className="is-link is-small" onClick={() => setModalOpen(true)} disabled={!editMode}>
                        <Icon icon={faPlus} />
                        <span>Add Audience</span>
                    </Button>
                </div>
            </div>

            <p className="has-text-grey is-size-7 mb-2">
                Audiences define who is eligible for this experiment using attribute rules (device type, sign-in status, locale, etc.).
            </p>
            <p className="has-text-grey is-size-7 mb-3">
                <strong>All audiences are ANDed together.</strong> Include = user must match; Exclude = user must not match. To combine conditions (e.g. locale AND device type), create one audience per attribute and add both here. <Link to="/thor/audiences" className="has-text-link">Manage audience library →</Link>
            </p>

            <div className="field mb-4">
                <label className="label is-small">
                    Audience Description<HelpTooltip text={HELP.audiences.description} />
                </label>
                <div className="control">
                    <textarea className="textarea is-small" rows={2}
                              value={audienceDescription}
                              disabled={!editMode}
                              style={!editMode ? { resize: 'none' } : undefined}
                              onChange={e => setAudienceDescription(e.target.value)}
                              placeholder="Describe the target audience for this experiment…" />
                </div>
                {editMode && (
                    <div className="mt-1">
                        <Button className="is-link is-small" onClick={handleSaveDescription} disabled={isSavingDesc}>
                            {isSavingDesc ? 'Saving…' : 'Save Description'}
                        </Button>
                    </div>
                )}
            </div>

            <hr className="my-3" />

            <StateHandler showLoader={audiencesState.loading} showError={audiencesState.error}
                          loadingScreen={<SmallLoadingSpinner />}>
                {audiences.length === 0
                    ? (
                        <div className="has-text-centered" style={{ padding: '2rem', color: '#888' }}>
                            <p className="has-text-weight-semibold mb-2" style={{ color: '#363636' }}>No audience rules yet</p>
                            <p className="is-size-7 mb-3">Audience rules define <strong>who is eligible</strong> to be bucketed into your experiment (e.g., only logged-in users, only users in a specific locale). Leave empty to target all eligible users.</p>
                            {editMode && <button type="button" className="button is-link is-small" onClick={() => setModalOpen(true)}>Add audience rule</button>}
                            {!editMode && <p className="is-size-7 has-text-grey">Enable Edit mode to add audience rules.</p>}
                        </div>
                    )
                    : (
                        <table className="table is-fullwidth is-striped is-hoverable">
                            <thead>
                                <tr>
                                    <th>Audience</th>
                                    <th>Attribute</th>
                                    <th>Values</th>
                                    <th>Operation</th>
                                    <th>Type<HelpTooltip text={HELP.audiences.exclude} position="top" /></th>
                                    <th>Strict<HelpTooltip text={HELP.audiences.strict} position="top" /></th>
                                    <th className="has-text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {audiences.map(a => (
                                    <tr key={a.audienceId}>
                                        <td className="has-text-weight-semibold">
                                            <Link to={`/thor/audiences?q=${encodeURIComponent(a.audienceName)}`} className="has-text-dark" title="View in Audience Library">
                                                {a.audienceName}
                                            </Link>
                                        </td>
                                        <td>{a.attributeName || '—'}</td>
                                        <td>
                                            {a.attributeValues
                                                ? a.attributeValues.split(',').map((v, i) => (
                                                    <span key={i} className="tag is-light is-small mr-1 mb-1">{v.trim()}</span>
                                                  ))
                                                : <span className="tag is-danger is-light is-small">no values</span>
                                            }
                                        </td>
                                        <td>
                                            {a.operation
                                                ? <span className="tag is-light" title={a.operation}>{OPERATION_LABELS[a.operation] ?? a.operation}</span>
                                                : '—'}
                                        </td>
                                        <td>
                                            <span className={`tag ${(a.exclude ?? a.isExclude) ? 'is-danger is-light' : 'is-success is-light'}`}>
                                                {(a.exclude ?? a.isExclude) ? 'Exclude' : 'Include'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`tag ${(a.strict ?? a.isStrict) ? 'is-warning is-light' : 'is-light'}`}>
                                                {(a.strict ?? a.isStrict) ? 'Strict' : 'Non-strict'}
                                            </span>
                                        </td>
                                        <td className="has-text-right">
                                            <button type="button"
                                                className="button is-small is-ghost has-text-danger"
                                                aria-label="Remove audience"
                                                style={{ padding: '0 0.25rem' }}
                                                disabled={!editMode || deletingId === a.audienceId}
                                                onClick={editMode ? () => setConfirmDelete(a) : undefined}>
                                                <Icon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </StateHandler>

            <AudienceModal
                isActive={modalOpen}
                experimentKey={experimentKey}
                isSubmitting={isSubmitting}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
            />

            <ConfirmDialog
                isActive={confirmDelete !== null}
                title="Remove Audience"
                message={`Remove audience "${confirmDelete?.audienceName}"? This cannot be undone.`}
                confirmLabel="Remove"
                confirmClass="is-danger"
                onConfirm={() => { handleDelete(confirmDelete); setConfirmDelete(null); }}
                onCancel={() => setConfirmDelete(null)}
            />
        </>
    );
};

export default AudiencesTab;
